import { useContext } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import format from 'date-fns/format';
import { format as timezoneFormat } from 'date-fns-tz';

import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';
import { CONTEXT_PATH } from '@confluence/named-routes';
import { knownEditorError } from '@confluence/publish-page-as-blog/entry-points/errors';
import { useConvertPageToBlog } from '@confluence/publish-page-as-blog/entry-points/useConvertToBlog';
import { PublishPageAsBlogSources } from '@confluence/publish-page-as-blog';
import { markErrorAsHandled } from '@confluence/graphql';
import { buildEditorFlagMessage } from '@confluence/editor-flags';
import {
	AutoConvertEvent,
	AutoConvertActionSubject,
	sendEditorConversionAnalyticsPageEvent,
	useAutoConvertState,
} from '@confluence/editor-conversion';
import {
	EDITOR_UPDATE_SHORTCUT_IN_MODAL_PRESS,
	EDITOR_UPDATE_BUTTON_IN_MODAL_CLICK,
	setViewTransitionSource,
} from '@confluence/browser-metrics';
import { useSessionData } from '@confluence/session-data';
import { runCrossJoinThirdPartyNudgeExperiment } from '@confluence/experiment-cross-join-third-party-nudges';

import { PublishModalContext } from './PublishModalContext';
import { usePublishModalAnalyticsEvent } from './usePublishModalAnalyticsEvent';
import { cacheSchedulePublishTime, convertRestrictionsObjForContentREST } from './publishHelpers';
import { publishPageAsBlogFailureFlag } from './PublishModal/PublishAsBlogHelpers';

type HandlePublishProps = {
	analyticsSource: string;
	closeModal?: (params: { endPublishExperience: boolean }) => void;
	isUnpublishedDraft?: boolean;
	source?: string;
	shouldNotifyOverride?: boolean;
};

type HandlePreviewProps = {
	analyticsSource: string;
	isUnpublishedDraft?: boolean;
};

export type UsePublishModalActionsType = {
	handlePublish: (arg0: HandlePublishProps) => Promise<void>;
	handlePreview: (arg0: HandlePreviewProps) => Promise<void>;
};

const i18n = defineMessages({
	scheduledPublishMessage: {
		id: 'publish-modal.schedule.publish.message',
		description:
			'When a page is scheduled to be published and schedule is re-clicked without any changes to publish settings, this flag message will include the date, time, and whether it is scheduled during a.m. or p.m.',
		defaultMessage: '{pageTitle} will be published on {date} at {time}',
	},
	scheduledUpdateMessage: {
		id: 'publish-modal.schedule.update.message',
		description:
			'When a page is scheduled to be updated and schedule is re-clicked without any changes to publish settings, this flag message will include the date, time, and whether it is scheduled during a.m. or p.m.',
		defaultMessage: '{pageTitle} will be updated on {date} at {time}',
	},
	scheduledPublishTitle: {
		id: 'publish-modal.schedule.publish.title',
		description: 'Success title message when a page is scheduled to be published',
		defaultMessage: 'Publish scheduled',
	},
	scheduledUpdateTitle: {
		id: 'publish-modal.schedule.update.title',
		description: 'Success title message when a page is scheduled to be updated',
		defaultMessage: 'Update scheduled',
	},
});

export const usePublishModalActions = (): UsePublishModalActionsType => {
	const intl = useIntl();
	const { createAnalyticsEvent } = usePublishModalAnalyticsEvent();
	const convertPageToBlog = useConvertPageToBlog();
	const [isAutoConverted] = useAutoConvertState();

	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { push } = useRouteActions();
	const { userId, cloudId } = useSessionData();

	const {
		versionComments,
		publishAsBlog,
		shouldNotifyComputedValue,
		scheduledPublishDate,
		onClickPublish,
		onClickPreview,
		retrieveEditorTitleAndContent,
		flags,
		hasModifiedSettings,
		moveParams,
		restrictionsRestObj,
		hasModifiedRestrictions,
		isBlog,
	} = useContext(PublishModalContext);
	const closeMove = moveParams?.closeMove;

	const handleRescheduleWithNoChanges = async (
		closeModal: (params: { endPublishExperience: boolean }) => void,
		isUnpublishedDraft: boolean,
	) => {
		if (scheduledPublishDate) {
			const monthDayRegex = /[^,]*$/g; // selects everything after comma ","
			const date = format(scheduledPublishDate, 'PP').replace(monthDayRegex, ''); // Ex: Aug 29,
			const time = timezoneFormat(scheduledPublishDate, 'h:mm aaaa z'); // Ex: 11:00 a.m. PST
			const { title: pageTitle } = await retrieveEditorTitleAndContent();

			closeModal({ endPublishExperience: true });

			void flags.showInfoFlag({
				title: intl.formatMessage(
					isUnpublishedDraft ? i18n.scheduledPublishTitle : i18n.scheduledUpdateTitle,
				),
				description: intl.formatMessage(
					isUnpublishedDraft ? i18n.scheduledPublishMessage : i18n.scheduledUpdateMessage,
					{ pageTitle, date, time },
				),
			});
		}
	};

	const handlePublishWithoutConversion = ({
		analyticsSource,
		closeModal,
		isUnpublishedDraft,
		shouldNotifyOverride,
	}) => {
		// if page is already scheduled to be updated and no publish setting are changed,
		// we can just confirm the existing scheduled publish
		if (!hasModifiedSettings && !!scheduledPublishDate && closeModal) {
			void handleRescheduleWithNoChanges(closeModal, isUnpublishedDraft);
			return;
		}

		// This makes the publish modal publish button switch to "Scheduled" right away after completing a schedule publish.
		// This is achieved by temporarily writing the new schedule publish date into the GraphQL cache.
		// Apollo will still re-query though to get the true result from the server, is case of failure.
		if (!!scheduledPublishDate && contentId) {
			cacheSchedulePublishTime({
				contentId,
				schedulePublishDate: scheduledPublishDate?.toISOString(),
			});
		}

		const newAncestors: { id: string }[] = [];
		if (moveParams && moveParams.newParentPageId) {
			const newParentPageId = moveParams?.newParentPageId;
			// Backend only need the direct parent page id in order to move a page
			// Could send the entire ancestor array but decided to keep the request payload small
			newAncestors.push({ id: newParentPageId });
		}
		const newSpaceKey =
			isBlog || (moveParams && moveParams.newParentPageId) ? moveParams?.newSpaceKey : undefined;

		const notifyWatchers = shouldNotifyOverride ?? shouldNotifyComputedValue;

		onClickPublish({
			suppressNotifyWatchers: !notifyWatchers,
			message: versionComments,
			schedulePublishDate: !!scheduledPublishDate ? scheduledPublishDate?.toISOString() : null,
			ancestors: newAncestors,
			newSpaceKey,
			moveRequest: {
				targetId: moveParams?.targetId,
				position: moveParams?.position,
				previousParentPageId: moveParams?.previousParentPageId,
			},
			restrictions:
				hasModifiedRestrictions && restrictionsRestObj
					? convertRestrictionsObjForContentREST(restrictionsRestObj)
					: undefined,
		})();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'PublishModalPublishButton',
				source: analyticsSource,
				attributes: {
					notifyWatchers,
					hasModifiedRestrictions,
					hasNewSpaceKey:
						moveParams?.newSpaceKey !== undefined && moveParams?.newSpaceKey !== spaceKey,
					hasNewParentPage: newAncestors.length !== 0,
					hasComments: !!versionComments,
					isSchedulePublish: !!scheduledPublishDate,
					isUnpublishedDraft: !!isUnpublishedDraft,
				},
			},
		}).fire();

		isAutoConverted &&
			sendEditorConversionAnalyticsPageEvent(
				createAnalyticsEvent,
				{
					pageAction: AutoConvertEvent.PUBLISHED,
					source: analyticsSource,
				},
				AutoConvertActionSubject.AUTO_CONVERSION,
			);
	};
	const publishPageAsBlogSuccess = (data) => {
		const newlyCreatedBlogUrl = data?.experimentalPublishPageAsBlog?._links?.webui;
		push(`${CONTEXT_PATH}${newlyCreatedBlogUrl}`, true);
	};
	const publishPageAsBlogPublishFailure = ({ links, errorMessage, flags: callbackFlags }) => {
		// We know what happened, so send an error flag then push the user over to the edit page for
		// the recently converted blog
		publishPageAsBlogFailureFlag({
			flags: callbackFlags,
			attributes: { error: errorMessage },
		});

		push(`${CONTEXT_PATH}${links?.editui}`, true);
	};
	const conversionFromPageToBlogFailure =
		(isUnpublishedDraft) =>
		({ error, flags: callbackFlags }) => {
			const errorMessage = `${error?.message || error}`;
			const knownEditorErrorMessage = knownEditorError(errorMessage);

			if (knownEditorErrorMessage) {
				publishPageAsBlogFailureFlag({
					flags: callbackFlags,
					attributes: { error: knownEditorErrorMessage },
					customFlagMessage: buildEditorFlagMessage({
						errorData: { [knownEditorErrorMessage]: true },
						spaceKey,
						formatMessage: intl.formatMessage,
						statusCode: undefined,
						nextTryIn: 0,
						isUnpublishedDraft,
					}),
				});
			} else {
				publishPageAsBlogFailureFlag({
					flags: callbackFlags,
					attributes: { error: errorMessage },
				});
			}

			markErrorAsHandled(error);
		};
	const handlePublishAsBlogClick = async ({ analyticsSource, isUnpublishedDraft }) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'PublishModalPublishAsBlog',
				source: analyticsSource,
				attributes: {
					notifyWatchers: shouldNotifyComputedValue,
					hasModifiedRestrictions,
					hasNewSpaceKey:
						moveParams?.newSpaceKey !== undefined && moveParams?.newSpaceKey !== spaceKey,
					hasComments: !!versionComments,
					isSchedulePublish: !!scheduledPublishDate,
					isUnpublishedDraft: !!isUnpublishedDraft,
				},
			},
		}).fire();

		await convertPageToBlog({
			flags,
			sourceAction: PublishPageAsBlogSources.EditPage,
			contentId: contentId || '',
			spaceKey: moveParams?.newSpaceKey || spaceKey,
			message: versionComments,
			suppressNotifyWatchers: !shouldNotifyComputedValue,
			schedulePublishDate: scheduledPublishDate?.toISOString(),
			retrieveEditorTitleAndContent,
			onConvertAndPublishSucceed: publishPageAsBlogSuccess,
			onPublishFail: publishPageAsBlogPublishFailure,
			onConvertFail: conversionFromPageToBlogFailure(isUnpublishedDraft),
			restrictions:
				hasModifiedRestrictions && restrictionsRestObj
					? convertRestrictionsObjForContentREST(restrictionsRestObj)
					: undefined,
		});
	};
	const handlePublish = async ({
		analyticsSource,
		closeModal,
		isUnpublishedDraft,
		source = '',
		shouldNotifyOverride,
	}: HandlePublishProps) => {
		if (publishAsBlog) {
			void handlePublishAsBlogClick({ analyticsSource, isUnpublishedDraft });
		} else {
			void runCrossJoinThirdPartyNudgeExperiment({
				cloudId,
				userId,
				createAnalyticsEvent,
			});
			if (source) {
				let trigger = EDITOR_UPDATE_BUTTON_IN_MODAL_CLICK;

				if (source === 'shortcut') {
					trigger = EDITOR_UPDATE_SHORTCUT_IN_MODAL_PRESS;
				}

				setViewTransitionSource(trigger);
			}
			void handlePublishWithoutConversion({
				analyticsSource,
				closeModal,
				isUnpublishedDraft,
				shouldNotifyOverride,
			});
		}
		closeModal?.({ endPublishExperience: true });
		closeMove?.();
	};
	const handlePreview = async ({ analyticsSource, isUnpublishedDraft }: HandlePreviewProps) => {
		onClickPreview(publishAsBlog, moveParams);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'PublishModalPreviewButton',
				source: analyticsSource,
				attributes: {
					isUnpublishedDraft: !!isUnpublishedDraft,
				},
			},
		}).fire();
		closeMove?.();
	};

	return {
		handlePreview,
		handlePublish,
	};
};
